import React, { useEffect, useState } from "react";
import { BiChevronDown } from "react-icons/bi";

import { useOnClickOutside } from "../../hooks/useOutsideClick";
import { useTranslation } from "react-i18next";

interface IColorDropDown {
  acceptableColors: Array<{ label: string; value: string }>;
  disabled?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  currentColor: string;
  onColorChanged: (color: string) => void;
}

const ColorDropDown = ({
  acceptableColors,
  disabled = false,
  isInvalid = false,
  errorMessage = "",
  currentColor,
  onColorChanged
}: IColorDropDown) => {
  const [showMenu, setShowMenu] = useState(false);
  const { t, i18n } = useTranslation();
  const [uniqueColorSet, setUniqueColorSet] = useState<
    Array<{ label: string; value: string }>
  >([]);

  const [inputColor, setInputColor] = useState<string>("");

  const ref = React.useRef(null);

  useOnClickOutside(ref, () => {
    setShowMenu(false);
  });

  useEffect(() => {
    const uniqueSet = new Set<string>();
    const filteredObjects = acceptableColors.filter((obj) => {
      if (!uniqueSet.has(obj.value)) {
        uniqueSet.add(obj.value);
        return true;
      }
      return false;
    });

    setUniqueColorSet(filteredObjects);
  }, [acceptableColors]);

  useEffect(() => {
    if (
      !uniqueColorSet.find(
        (c) => c.label.toLowerCase() === currentColor.toLowerCase()
      )?.value
    ) {
      setInputColor(currentColor);
    }
  }, [currentColor]);

  function isValidColorCode(input: string) {
    // Regular expression to match valid 3-digit or 6-digit hex color codes
    const colorCodeRegex = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

    return colorCodeRegex.test(input);
  }

  return (
    <div className="custom-color-dropdown tw-relative" ref={ref}>
      {/* Select color */}
      <div
        className={`tw-h-[38px]   ${
          disabled ? "tw-bg-gray-100 tw-cursor-not-allowed" : "tw-bg-white"
        } tw-rounded-md tw-shadow tw-border-solid tw-border ${
          isInvalid ? "tw-border-red-600" : "tw-border-gray-300"
        } tw-justify-start tw-items-center tw-flex tw-self-stretch`}
        onClick={() => !disabled && setShowMenu(!showMenu)}
        onKeyDown={() => !disabled && setShowMenu(!showMenu)}
        role="button"
        tabIndex={0}
      >
        <div className="tw-grow tw-shrink tw-basis-0 tw-h-[38px] tw-pl-[13px] tw-pr-3 tw-py-[9px] tw-justify-start tw-items-center tw-gap-2 tw-flex">
          {currentColor ? (
            <React.Fragment>
              <div
                className="tw-w-4 tw-h-4 tw-rounded-[10px] tw-border tw-border-solid tw-border-gray-300 tw-shrink-0"
                style={{
                  backgroundColor:
                    uniqueColorSet.find(
                      (c) =>
                        c.label.toLowerCase() === currentColor.toLowerCase()
                    )?.value ?? currentColor
                }}
              />
              <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                {uniqueColorSet.find(
                  (c) => c.label.toLowerCase() === currentColor.toLowerCase()
                )?.label ?? currentColor}
              </div>
            </React.Fragment>
          ) : (
            <div className="tw-text-gray-400 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
              Select color
            </div>
          )}
        </div>
        <div className="tw-px-[13px] tw-py-[9px] tw-justify-center tw-items-center tw-gap-1 tw-flex tw-flex-col">
          <BiChevronDown size={22} />
        </div>
      </div>
      {/* Error Message */}
      {isInvalid && (
        <div className="tw-text-red-600 tw-text-sm tw-mt-1">{errorMessage}</div>
      )}
      {/* Menu */}
      <div
        className={`tw-w-full tw-max-h-[632px] tw-overflow-auto tw-py-2 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-solid tw-border-gray-300 tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-inline-flex ${
          showMenu
            ? "tw-block tw-absolute tw-top-11 tw-z-10 tw-transition-all transition-duration-300 tw-h-max"
            : "tw-hidden tw-h-0"
        }`}
      >
        <div className="tw-self-stretch tw-max-h-[616px] tw-px-2 tw-flex-col tw-justify-start tw-items-start tw-flex">
          {/* TODO => Custom picker */}
          {/* <div className="tw-self-stretch tw-px-4 tw-py-2.5 tw-rounded tw-justify-start tw-items-center tw-gap-3 tw-inline-flex">
            <div className="tw-p-0.5 tw-justify-start tw-items-center tw-gap-2.5 tw-flex">
              <div className="tw-w-5 tw-h-5 tw-relative" />
            </div>
            <div className="tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
              Pick custom color
            </div>
          </div> */}
          {uniqueColorSet.map((color) => (
            <div
              key={color.value}
              className="tw-self-stretch tw-px-4 tw-py-2.5 tw-rounded tw-justify-start tw-items-center tw-gap-3 tw-inline-flex"
              onClick={() => {
                onColorChanged(color.label.toLowerCase());
                setShowMenu(false);
              }}
              onKeyDown={() => {
                onColorChanged(color.label.toLowerCase());
                setShowMenu(false);
              }}
              role="button"
              tabIndex={0}
            >
              <div
                className={`tw-w-4 tw-h-4 tw-rounded-[10px] tw-border tw-border-solid tw-border-gray-300 tw-shrink-0`}
                style={{ backgroundColor: color.value }}
              />
              <div className="tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                {color.label}
              </div>
            </div>
          ))}
          <div
            className="tw-self-stretch tw-px-4 tw-py-2.5 tw-rounded tw-justify-start tw-items-center tw-gap-3 tw-inline-flex"
            role="button"
            tabIndex={0}
          >
            <div
              style={{
                backgroundColor: isValidColorCode(inputColor)
                  ? inputColor
                  : "#000"
              }}
              className={`tw-w-4 tw-h-4 tw-rounded-[10px] tw-border tw-border-solid tw-shrink-0`}
            />
            <div className="tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal tw-flex tw-gap-3 tw-items-center">
              <input
                placeholder={t("Wizard/general/key15")}
                value={isValidColorCode(inputColor) ? inputColor : undefined}
                onChange={(e) => {
                  setInputColor(e.target.value);
                }}
                className="tw-w-full tw-h-9 tw-px-3 tw-py-2 tw-bg-white tw-rounded tw-border tw-border-gray-300 tw-justify-start tw-items-center tw-gap-3 tw-inline-flex "
              />
              <div
                onClick={() => {
                  if (isValidColorCode(inputColor)) {
                    onColorChanged(inputColor);
                    setShowMenu(false);
                  }
                }}
                onKeyDown={() => {
                  if (isValidColorCode(inputColor)) {
                    onColorChanged(inputColor);
                    setShowMenu(false);
                  }
                }}
                className={`lg:tw-px-5 tw-px-2 tw-py-2 tw-shadow tw-justify-center tw-items-center tw-inline-flex tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal tw-border-none tw-ring-0 tw-rounded-full  tw-text-white ${
                  isValidColorCode(inputColor)
                    ? " tw-bg-primary"
                    : " tw-bg-gray-300 tw-text-white"
                }`}
              >
                select
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorDropDown;
